'use strict';

const libPictProvider = require('pict-provider');
// Shared marked-based user-markdown renderer (HTML-escaped + safe URLs) — the same one the staff console + the rest of
// the app use, so a customer's case Details and the acknowledgement/resolution comments render fully (blockquotes,
// lists, links) instead of the old bold/italic/code-only subset.
const libUserMarkdown = require('../../../../lib/web/headlight-dynamic-inputs-bundle/source/providers/_UserMarkdown.js');

// Customer-facing labels (a stable subset mirrored from the staff console; kept local so this read-only surface has no
// dependency on the console provider). The portal never shows internal-only fields (resolution notes, follow-ups, agent).
// The internal tiers (Acknowledged / Tier 1-3) are deliberately collapsed to a single caller-facing "In progress" —
// the portal never exposes the support team's triage/escalation level.
const STATUS_META =
{
	New:          { label: 'Open',        cls: 'spx-st-open', icon: 'fa-inbox' },
	Acknowledged: { label: 'In progress', cls: 'spx-st-prog', icon: 'fa-person-digging' },
	Tier1:        { label: 'In progress', cls: 'spx-st-prog', icon: 'fa-person-digging' },
	Tier2:        { label: 'In progress', cls: 'spx-st-prog', icon: 'fa-person-digging' },
	Tier3:        { label: 'In progress', cls: 'spx-st-prog', icon: 'fa-person-digging' },
	Resolved:     { label: 'Resolved',    cls: 'spx-st-done', icon: 'fa-circle-check' },
};
const OPEN_STATUSES = [ 'New', 'Acknowledged', 'Tier1', 'Tier2', 'Tier3' ];
const PRIORITY_META =
{
	Urgent: { label: 'Urgent', cls: 'spx-prio-urgent' },
	High:   { label: 'High',   cls: 'spx-prio-high' },
	Normal: { label: 'Normal', cls: 'spx-prio-normal' },
	Low:    { label: 'Low',    cls: 'spx-prio-low' },
};
const CATEGORY_LABELS =
{
	HowTo: 'How-to question', Bug: 'Something isn’t working', PasswordReset: 'Password reset',
	Access: 'Access / permissions', Data: 'Data question', Device: 'Device', Other: 'Other',
};
const APP_LABELS =
{
	Fieldbook: 'Fieldbook', Bridge: 'Bridge', Home: 'Home', LIMS: 'LIMS',
	Configuration: 'Configuration', Academy: 'Academy', Mobile: 'Mobile', Other: 'Other',
};
// The caller-facing comment kinds the portal surfaces (from Comment.Metadata.Kind). Internal notes / follow-ups / stage
// changes are NEVER shown here.
const CUSTOMER_COMM_KINDS = { Acknowledgement: { label: 'Acknowledged', icon: 'fa-paper-plane' }, Resolution: { label: 'Resolution', icon: 'fa-circle-check' } };
const PAGE_SIZE = 50;   // server-side page size; the portal pages through the customer's cases (Prev/Next)

/**
 * SupportPortalProvider — the read-only customer-facing open-cases dashboard (#/SupportPortal). It shows a customer's
 * own support calls (their organization's, or just the user's own, per config) with a curated, read-only set of fields
 * and the caller-facing updates (acknowledgement / resolution comments) — NO triage, NO create/edit, NO internal notes.
 *
 * Tenancy: support calls live in the central HeadLight queue (Document.IDCustomer = the support team, e.g. 1), NOT in
 * the tenant they concern — they are HeadLight cases ABOUT a customer, keyed to that customer by a `Customer`
 * DocumentPolyJoin. The plain Documents query is pinned by the server to Document.IDCustomer = the caller's customer,
 * which would hide every one of these from the very customer they are about, so this portal reads through the dedicated
 * `Document/Support/Cases/:Cap` endpoint. That endpoint reads at the repository level (outside the tenancy behavior) and
 * re-imposes scope itself: it returns only the SupportCall-archetype documents whose `Customer` polyjoin IDRemote equals
 * the caller's OWN customer, so a customer only ever sees calls about their own organization. The curated view is
 * configured by `SupportPortal/CustomerView` (Global⊕Customer cascade). See [[config-app-support-portal]].
 */
class SupportPortalProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._mount = '#SupportPortal-Host';
		this._loaded = false;
		this._loadGen = 0;
		this._config = null;                // { Scope:'customer'|'own', ShowResolution, ShowComms }
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get permissions() { return this.pict.fable.EffectivePermissionsService; }
	get modal() { return this.pict.views['Pict-Section-Modal']; }
	get idCustomer() { return Number((this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.CustomerID) || 0); }
	get idUser() { return Number((this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.UserID) || 0); }

	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pErr, pBody) => (pErr ? reject(pErr) : resolve(pBody)))); }
	_toast(pMessage, pType) { if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'info' }); } }
	_esc(pValue) { return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	/** Anyone with a support view/agent/manage grant (or a super-user) can see the portal. */
	canView()
	{
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.canHash !== 'function') || !tmpPermissions.currentUserReady()) { return true; }
		return tmpPermissions.canHash('Support-View') || tmpPermissions.canHash('Support-Agent') || tmpPermissions.canHash('Support-Manage');
	}

	get state()
	{
		if (!this.pict.AppData.SupportPortal)
		{
			this.pict.AppData.SupportPortal = { Loading: true, Error: '', Calls: [], Total: 0, Page: 0, Filter: 'open', Search: '', ViewMode: 'list', Active: null, Comms: { loading: false, items: [] } };
		}
		return this.pict.AppData.SupportPortal;
	}

	_date(pValue)
	{
		const tmpS = String(pValue == null ? '' : pValue);
		if (!tmpS || tmpS.indexOf('0000-00-00') === 0) { return '—'; }
		const tmpDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(tmpS);
		const tmpD = tmpDateOnly ? new Date(Number(tmpDateOnly[1]), Number(tmpDateOnly[2]) - 1, Number(tmpDateOnly[3])) : new Date(tmpS);
		return Number.isNaN(tmpD.getTime()) ? tmpS : tmpD.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
	}
	_dateTime(pValue)
	{
		const tmpS = String(pValue || ''); if (!tmpS) { return ''; }
		const tmpD = new Date(tmpS); if (Number.isNaN(tmpD.getTime())) { return tmpS; }
		return tmpD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + tmpD.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
	}
	_statusMeta(pStatus) { return STATUS_META[pStatus] || { label: pStatus || 'Open', cls: 'spx-st-open', icon: 'fa-circle' }; }
	_prioMeta(pPriority) { return PRIORITY_META[pPriority] || null; }

	async loadAsync()
	{
		if (!this.canView()) { this._paint(); return; }
		const tmpGen = ++this._loadGen;
		this.state.Loading = true; this.state.Error = '';
		this._paint();
		try
		{
			await this._loadConfigAsync();
			if (tmpGen !== this._loadGen) { return; }
			await this._loadCallsAsync();
			if (tmpGen !== this._loadGen) { return; }
			this.state.Loading = false;
			this._loaded = true;
		}
		catch (pError)
		{
			if (tmpGen !== this._loadGen) { return; }
			this.state.Loading = false;
			this.state.Error = (pError && pError.message) ? pError.message : String(pError);
		}
		this._paint();
	}

	async _loadConfigAsync()
	{
		let tmpView = null;
		try
		{
			const tmpBlob = await this._getJSON(`Configuration/For/SupportPortal/In/Customer/${this.idCustomer}`);
			if (tmpBlob && tmpBlob.CustomerView && typeof tmpBlob.CustomerView === 'object') { tmpView = tmpBlob.CustomerView; }
		}
		catch (pError) { /* defaults below */ }
		this._config = Object.assign({ Scope: 'customer', ShowResolution: true, ShowComms: true }, tmpView || {});
	}

	async _loadCallsAsync()
	{
		// Support calls sit in the central HeadLight queue, keyed to the customer they are ABOUT by a `Customer`
		// DocumentPolyJoin, so the plain tenancy-scoped Documents query cannot see them (see class header). The
		// Document/Support/Cases endpoint reads them by the SupportCall archetype and scopes to the caller's own
		// customer via that polyjoin. Returns Documents newest-first with FormData already parsed.
		// Server-side pagination: the endpoint returns { Records, Total } for the requested page + filters. Open/All
		// (status), search (title/#), and the 'own' scope are all applied server-side so paging is correct across the
		// customer's whole case set, not just a loaded slice.
		const tmpBegin = (this.state.Page || 0) * PAGE_SIZE;
		const tmpStatus = (this.state.Filter === 'all') ? 'all' : 'open';
		const tmpOwn = (this._config && this._config.Scope === 'own') ? '1' : '0';
		const tmpQ = String(this.state.Search || '').trim();
		const tmpURL = `Document/Support/Cases/${tmpBegin}/${PAGE_SIZE}?status=${tmpStatus}&own=${tmpOwn}`
			+ (tmpQ ? `&q=${encodeURIComponent(tmpQ)}` : '');
		const tmpBody = await this._getJSON(tmpURL);
		const tmpRecords = (tmpBody && Array.isArray(tmpBody.Records)) ? tmpBody.Records : (Array.isArray(tmpBody) ? tmpBody : []);
		// Never show a voided (spam) call to the customer. The server already excludes Void from the `open` set, but the
		// `all` view would return it, and the portal's unknown-status fallback would mislabel it 'Open' — so drop it here
		// and keep the page total self-consistent with what is rendered.
		const tmpVisible = tmpRecords.filter((pDoc) => String(pDoc && pDoc.Status) !== 'Void');
		const tmpDropped = tmpRecords.length - tmpVisible.length;
		this.state.Total = (tmpBody && typeof tmpBody.Total === 'number') ? Math.max(0, tmpBody.Total - tmpDropped) : tmpVisible.length;
		this.state.Calls = tmpVisible.map((pDoc) => this._summary(pDoc));
	}

	/** Reload the current page after a filter / search / page change. pFull repaints the whole shell (a deliberate
	 *  click — e.g. the Open/All toggle); otherwise only the list repaints, so a focused search box keeps its caret. */
	async _reloadAsync(pFull)
	{
		const tmpGen = ++this._loadGen;
		this.state.Loading = true;
		if (pFull) { this._paint(); } else { this._paintList(); }
		try
		{
			await this._loadCallsAsync();
			if (tmpGen !== this._loadGen) { return; }
			this.state.Loading = false;
		}
		catch (pError)
		{
			if (tmpGen !== this._loadGen) { return; }
			this.state.Loading = false;
			this.state.Error = (pError && pError.message) ? pError.message : String(pError);
		}
		this._paintList();
	}

	_summary(pDoc)
	{
		let tmpForm = pDoc.FormData;
		if (typeof tmpForm === 'string') { try { tmpForm = JSON.parse(tmpForm); } catch (pError) { tmpForm = {}; } }
		tmpForm = tmpForm || {};
		const tmpIssue = tmpForm.Issue || {};
		const tmpStatus = STATUS_META[pDoc.Status] ? pDoc.Status : 'New';
		return {
			IDDocument: Number(pDoc.IDDocument),
			Status: tmpStatus,
			Summary: tmpIssue.Summary || pDoc.Name || '(no summary)',
			Details: tmpIssue.Details || '',
			Category: tmpIssue.Category || '',
			Priority: tmpIssue.Priority || '',
			App: tmpIssue.App || '',
			Created: pDoc.CreateDate || '',
			Updated: pDoc.UpdateDate || pDoc.CreateDate || '',
		};
	}

	/** The current page's rows come straight from the server — filter, search, and paging are all server-side now. */
	_visibleCalls() { return this.state.Calls; }

	/** Prev/Next pager + "Showing X–Y of N" for the active filter/search; hidden when everything fits on one page. */
	_paginationHTML()
	{
		const tmpTotal = this.state.Total || 0;
		if (tmpTotal <= PAGE_SIZE) { return ''; }
		const tmpPages = Math.max(1, Math.ceil(tmpTotal / PAGE_SIZE));
		const tmpPage = this.state.Page || 0;
		const tmpStart = tmpPage * PAGE_SIZE + 1;
		const tmpEnd = Math.min(tmpTotal, (tmpPage + 1) * PAGE_SIZE);
		return `<div class="spx-pager">`
			+ `<button class="spx-pg" ${tmpPage <= 0 ? 'disabled' : ''} onclick="_Pict.providers.SupportPortal.setPage(${tmpPage - 1}); return false;"><i class="fas fa-chevron-left"></i> Prev</button>`
			+ `<span class="spx-pg-info">Showing <b>${tmpStart.toLocaleString()}–${tmpEnd.toLocaleString()}</b> of <b>${tmpTotal.toLocaleString()}</b></span>`
			+ `<button class="spx-pg" ${tmpPage >= (tmpPages - 1) ? 'disabled' : ''} onclick="_Pict.providers.SupportPortal.setPage(${tmpPage + 1}); return false;">Next <i class="fas fa-chevron-right"></i></button>`
			+ `</div>`;
	}

	setFilter(pFilter)
	{
		const tmpF = (pFilter === 'all') ? 'all' : 'open';
		if (tmpF === this.state.Filter) { return; }
		this.state.Filter = tmpF; this.state.Page = 0;
		this._reloadAsync(true);   // full repaint: updates the active toggle; it's a click, so there is no caret to preserve
	}
	onSearch(pValue)
	{
		this.state.Search = pValue || ''; this.state.Page = 0;
		if (this._searchTimer) { clearTimeout(this._searchTimer); }
		this._searchTimer = setTimeout(() => { this._searchTimer = null; this._reloadAsync(false); }, 300);   // list-only repaint keeps the search input focused
	}
	setPage(pPage)
	{
		const tmpMax = Math.max(0, Math.ceil((this.state.Total || 0) / PAGE_SIZE) - 1);
		const tmpP = Math.min(Math.max(0, Number(pPage) || 0), tmpMax);
		if (tmpP === (this.state.Page || 0)) { return; }
		this.state.Page = tmpP;
		this._reloadAsync(false);
	}

	// ── Read-only case detail ────────────────────────────────────────────────────────────────────────────────────

	async openCase(pIDDocument)
	{
		const tmpCall = this.state.Calls.find((pC) => pC.IDDocument === Number(pIDDocument));
		if (!tmpCall) { return; }
		this.state.Active = tmpCall;
		this.state.Comms = { loading: this._config && this._config.ShowComms, items: [] };
		this._paint();
		if (this._config && this._config.ShowComms) { this._loadCommsAsync(Number(pIDDocument)); }
	}

	backToList() { this.state.Active = null; this._paint(); }

	/** Load ONLY the caller-facing comments (acknowledgement / resolution) for the case — never internal notes. */
	async _loadCommsAsync(pIDDocument)
	{
		try
		{
			const tmpRows = await this._getJSON(`DocumentComments/FilteredTo/FBV~IDDocument~EQ~${pIDDocument}~FBV~Deleted~EQ~0/0/200`);
			if (!this.state.Active || this.state.Active.IDDocument !== pIDDocument) { return; }
			const tmpItems = [];
			(Array.isArray(tmpRows) ? tmpRows : []).forEach((pC) =>
			{
				let tmpMeta = {}; try { tmpMeta = pC.Metadata ? JSON.parse(pC.Metadata) : {}; } catch (pErr) { tmpMeta = {}; }
				if (!CUSTOMER_COMM_KINDS[tmpMeta.Kind]) { return; }   // caller-facing only
				tmpItems.push({ kind: tmpMeta.Kind, text: pC.Comment || '', at: pC.CreateDate || '' });
			});
			tmpItems.sort((pA, pB) => String(pB.at).localeCompare(String(pA.at)));
			this.state.Comms = { loading: false, items: tmpItems };
		}
		catch (pError) { this.state.Comms = { loading: false, items: [] }; }
		this._paintDetail();
	}

	// ── Render ───────────────────────────────────────────────────────────────────────────────────────────────────

	_paint() { const tmpCA = this.pict.ContentAssignment; if (tmpCA && tmpCA.getElement(this._mount).length) { tmpCA.assignContent(this._mount, this._shellHTML()); } }
	_paintList() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#SupportPortal-List').length) { tmpCA.assignContent('#SupportPortal-List', this._listHTML()); } }
	_paintDetail() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#SupportPortal-Detail').length) { tmpCA.assignContent('#SupportPortal-Detail', this._detailHTML()); } }

	_shellHTML()
	{
		if (!this.canView()) { return `<div class="spx-note"><i class="fas fa-lock"></i> You don’t have access to the support portal.</div>`; }
		if (this.state.Active) { return `<div id="SupportPortal-Detail">${this._detailHTML()}</div>`; }
		return `<div class="spx-toolbar">`
			+ `<div class="spx-seg">`
			+ `<button class="spx-seg-b ${this.state.Filter === 'open' ? 'spx-seg-on' : ''}" onclick="_Pict.providers.SupportPortal.setFilter('open'); return false;">Open</button>`
			+ `<button class="spx-seg-b ${this.state.Filter === 'all' ? 'spx-seg-on' : ''}" onclick="_Pict.providers.SupportPortal.setFilter('all'); return false;">All</button>`
			+ `</div>`
			+ `<div class="spx-toolbar-right">`
			+ `<div class="spx-search"><i class="fas fa-magnifying-glass"></i><input type="text" placeholder="Search your cases…" value="${this._esc(this.state.Search)}" oninput="_Pict.providers.SupportPortal.onSearch(this.value)" /></div>`
			+ `<div class="spx-viewtoggle">`
			+ `<button class="spx-vt-b ${this.state.ViewMode !== 'cards' ? 'spx-vt-on' : ''}" title="List view" onclick="_Pict.providers.SupportPortal.setViewMode('list'); return false;"><i class="fas fa-list-ul"></i></button>`
			+ `<button class="spx-vt-b ${this.state.ViewMode === 'cards' ? 'spx-vt-on' : ''}" title="Card view" onclick="_Pict.providers.SupportPortal.setViewMode('cards'); return false;"><i class="fas fa-table-cells-large"></i></button>`
			+ `</div>`
			+ `</div>`
			+ `</div>`
			+ (this.state.Error ? `<div class="spx-note spx-note-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this.state.Error)}</div>` : '')
			+ `<div id="SupportPortal-List">${this._listHTML()}</div>`;
	}

	_listHTML()
	{
		if (this.state.Loading) { return `<div class="spx-note"><i class="fas fa-spinner fa-spin"></i> Loading your support cases…</div>`; }
		const tmpRows = this.state.Calls;
		if (!tmpRows.length)
		{
			const tmpFiltered = !!String(this.state.Search || '').trim() || this.state.Filter === 'open';
			const tmpMsg = tmpFiltered ? 'No cases match.' : 'No support cases on file for your organization yet.';
			return `<div class="spx-empty"><i class="fas fa-inbox"></i> ${tmpMsg}</div>`;
		}
		const tmpBody = (this.state.ViewMode === 'cards')
			? `<div class="spx-cards">` + tmpRows.map((pCall) => this._cardHTML(pCall)).join('') + `</div>`
			: `<div class="spx-list">` + tmpRows.map((pCall) => this._rowHTML(pCall)).join('') + `</div>`;
		return tmpBody + this._paginationHTML();
	}

	setViewMode(pMode) { this.state.ViewMode = (pMode === 'cards') ? 'cards' : 'list'; this._paint(); }

	/** A compact list row (the default view). */
	_rowHTML(pCall)
	{
		const tmpStatus = this._statusMeta(pCall.Status);
		const tmpPrio = this._prioMeta(pCall.Priority);
		const tmpTags = [];
		if (pCall.Category) { tmpTags.push(`<span class="spx-tag">${this._esc(CATEGORY_LABELS[pCall.Category] || pCall.Category)}</span>`); }
		if (pCall.App) { tmpTags.push(`<span class="spx-tag spx-tag-app">${this._esc(APP_LABELS[pCall.App] || pCall.App)}</span>`); }
		if (tmpPrio) { tmpTags.push(`<span class="spx-prio ${tmpPrio.cls}">${this._esc(tmpPrio.label)}</span>`); }
		return `<button class="spx-row" onclick="_Pict.providers.SupportPortal.openCase(${pCall.IDDocument}); return false;">`
			+ `<span class="spx-row-badge ${tmpStatus.cls}"><i class="fas ${tmpStatus.icon}"></i> ${tmpStatus.label}</span>`
			+ `<span class="spx-row-main"><span class="spx-row-sum">${this._esc(pCall.Summary)}</span>`
			+ (tmpTags.length ? `<span class="spx-row-tags">${tmpTags.join('')}</span>` : '')
			+ `</span>`
			+ `<span class="spx-row-updated"><i class="fas fa-clock"></i> ${this._date(pCall.Updated)}</span>`
			+ `<span class="spx-row-id">#${pCall.IDDocument}</span>`
			+ `<i class="fas fa-chevron-right spx-row-chev"></i>`
			+ `</button>`;
	}

	_cardHTML(pCall)
	{
		const tmpStatus = this._statusMeta(pCall.Status);
		const tmpPrio = this._prioMeta(pCall.Priority);
		const tmpTags = [];
		if (pCall.Category) { tmpTags.push(`<span class="spx-tag">${this._esc(CATEGORY_LABELS[pCall.Category] || pCall.Category)}</span>`); }
		if (pCall.App) { tmpTags.push(`<span class="spx-tag spx-tag-app">${this._esc(APP_LABELS[pCall.App] || pCall.App)}</span>`); }
		if (tmpPrio) { tmpTags.push(`<span class="spx-prio ${tmpPrio.cls}">${this._esc(tmpPrio.label)}</span>`); }
		return `<button class="spx-card" onclick="_Pict.providers.SupportPortal.openCase(${pCall.IDDocument}); return false;">`
			+ `<div class="spx-card-top"><span class="spx-badge ${tmpStatus.cls}"><i class="fas ${tmpStatus.icon}"></i> ${tmpStatus.label}</span><span class="spx-card-id">#${pCall.IDDocument}</span></div>`
			+ `<div class="spx-card-sum">${this._esc(pCall.Summary)}</div>`
			+ `<div class="spx-card-tags">${tmpTags.join('')}</div>`
			+ `<div class="spx-card-foot"><i class="fas fa-clock"></i> Updated ${this._date(pCall.Updated)}</div>`
			+ `</button>`;
	}

	_detailHTML()
	{
		const tmpCall = this.state.Active;
		if (!tmpCall) { return ''; }
		const tmpStatus = this._statusMeta(tmpCall.Status);
		const tmpPrio = this._prioMeta(tmpCall.Priority);
		const tmpMeta = [];
		tmpMeta.push(`<div class="spx-meta"><span class="spx-meta-l">Status</span><span class="spx-badge ${tmpStatus.cls}"><i class="fas ${tmpStatus.icon}"></i> ${tmpStatus.label}</span></div>`);
		if (tmpCall.Category) { tmpMeta.push(`<div class="spx-meta"><span class="spx-meta-l">Category</span><span class="spx-tag">${this._esc(CATEGORY_LABELS[tmpCall.Category] || tmpCall.Category)}</span></div>`); }
		if (tmpCall.App) { tmpMeta.push(`<div class="spx-meta"><span class="spx-meta-l">Application</span><span class="spx-tag spx-tag-app">${this._esc(APP_LABELS[tmpCall.App] || tmpCall.App)}</span></div>`); }
		if (tmpPrio) { tmpMeta.push(`<div class="spx-meta"><span class="spx-meta-l">Priority</span><span class="spx-prio ${tmpPrio.cls}">${this._esc(tmpPrio.label)}</span></div>`); }
		tmpMeta.push(`<div class="spx-meta"><span class="spx-meta-l">Opened</span><span>${this._date(tmpCall.Created)}</span></div>`);
		tmpMeta.push(`<div class="spx-meta"><span class="spx-meta-l">Updated</span><span>${this._date(tmpCall.Updated)}</span></div>`);

		const tmpDetails = tmpCall.Details
			? `<div class="spx-det-card"><div class="spx-det-h">What you told us</div><div class="spx-det-body">${this._renderMarkdownSafe(tmpCall.Details)}</div></div>`
			: '';
		const tmpUpdates = (this._config && this._config.ShowComms) ? this._commsHTML() : '';

		return `<a class="spx-back" onclick="_Pict.providers.SupportPortal.backToList(); return false;"><i class="fas fa-arrow-left"></i> All cases</a>`
			+ `<div class="spx-det-head"><span class="spx-card-id">#${tmpCall.IDDocument}</span><h2 class="spx-det-title">${this._esc(tmpCall.Summary)}</h2></div>`
			+ `<div class="spx-det-meta">${tmpMeta.join('')}</div>`
			+ tmpDetails
			+ tmpUpdates;
	}

	_commsHTML()
	{
		const tmpC = this.state.Comms || { loading: false, items: [] };
		let tmpBody;
		if (tmpC.loading) { tmpBody = `<div class="spx-note"><i class="fas fa-spinner fa-spin"></i> Loading updates…</div>`; }
		else if (!tmpC.items.length) { tmpBody = `<div class="spx-empty spx-empty-sm">No updates from the support team yet.</div>`; }
		else
		{
			tmpBody = tmpC.items.map((pIt) =>
			{
				const tmpKind = CUSTOMER_COMM_KINDS[pIt.kind] || { label: 'Update', icon: 'fa-comment' };
				return `<div class="spx-update"><span class="spx-update-icon spx-uk-${pIt.kind === 'Resolution' ? 'res' : 'ack'}"><i class="fas ${tmpKind.icon}"></i></span>`
					+ `<div class="spx-update-body"><div class="spx-update-head"><b>${tmpKind.label}</b><span class="spx-update-when">${this._dateTime(pIt.at)}</span></div>`
					+ `<div class="spx-update-text">${this._esc(pIt.text)}</div></div></div>`;
			}).join('');
		}
		return `<div class="spx-det-card"><div class="spx-det-h"><i class="fas fa-comments"></i> Updates from support</div><div class="spx-updates">${tmpBody}</div></div>`;
	}

	/** Minimal, safe markdown → HTML for the caller's own details (escape first, then a few inline/blocky bits). */
	_renderMarkdownSafe(pText)
	{
		const tmpSource = String(pText == null ? '' : pText);
		if (!tmpSource.trim()) { return ''; }
		try { return libUserMarkdown.renderUserMarkdown(tmpSource); }
		catch (pError) { return `<p>${this._esc(tmpSource)}</p>`; }
	}
}

module.exports = SupportPortalProvider;
module.exports.default_configuration = { ProviderIdentifier: 'SupportPortal' };
